import {
  Autocomplete,
  InputAdornment,
  TextField,
  IconButton,
  Box,
} from "@mui/material";
import { useEasierTheme } from "datalign-themes";
import { FacetFields } from "models/facet-fields";
import Input from "models/input/input";
import React from "react";
import { useNavigate } from "react-router-dom";
import SearchBarFacets from "../search-bar-facets";

const BaseSearchBar = ({
  facets,
  isLoading,
  inputsList,
  searchTerm,
  open,
  handleOpen,
  handleSearchTerm,
  handleSearch,
}: {
  facets: FacetFields;
  isLoading: boolean;
  inputsList: Input[];
  searchTerm: string;
  open: boolean;
  handleOpen: (open: boolean) => void;
  handleSearchTerm: (searchTerm: string) => void;
  handleSearch: () => void;
}) => {
  const navigate = useNavigate();
  const theme = useEasierTheme();

  const searchButtonStyles = {
    height: "56px",
    width: "56px",
    borderTopLeftRadius: "0%",
    borderBottomLeftRadius: "0%",
    mr: -1,
    "&:hover": {
      background: theme?.Colour.InversePrimary(),
      color: theme?.Colour.OnPrimaryContainer(),
    },
    ...(["FAF-nt", "nrdata-orig"].includes(
      process.env.REACT_APP_STYLING_THEME ?? "",
    )
      ? {
          backgroundColor: "#e35205",
          color: theme?.Colour.OnSecondary(),
          borderColor: theme?.Colour.Primary(),
          borderWidth: "1px",
          borderStyle: "solid",
        }
      : {
          backgroundColor: theme?.Colour.Primary(),
          color: theme?.Colour.OnPrimary(),
        }),
  };

  const textFieldStyles = {
    borderRadius: "40px",
    height: "56px",
    color: theme?.Colour.OnSurfaceVariant(),
    backgroundColor: ["FAF-nt", "nrdata-orig"].includes(
      process.env.REACT_APP_STYLING_THEME ?? "",
    )
      ? theme?.Colour.SurfaceContainerLowest()
      : theme?.Colour.SurfaceContainerLow(),
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "50vw" },
        maxWidth: "95vw",
        borderRadius: "40px",
        alignSelf: "center",
      }}
      mx={2}
    >
      <Autocomplete
        id="search-bar"
        loading={isLoading}
        disableClearable
        clearOnBlur={false}
        options={inputsList}
        inputValue={searchTerm ?? ""}
        getOptionLabel={(option) => option?.title ?? ""}
        onChange={(_e, newValue) =>
          newValue?.input_id && navigate(`/view/${newValue.input_id}`)
        }
        onInputChange={(_e, newValue) => handleSearchTerm(newValue ?? "")}
        onKeyDown={(event) => {
          if (event.code === "Enter") {
            handleSearch();
          }
        }}
        filterOptions={(x) => x}
        sx={{
          width: "100%",
          minWidth: { xs: "100%", sm: 300 },
          "&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root": {
            padding: "8px",
          },
        }}
        open={open} // Control dropdown visibility manually
        onOpen={() => handleOpen(true)}
        onClose={(event, reason) => {
          // Prevent closing if clicked inside the dropdown
          if (reason === "escape" || reason === "blur") {
            handleOpen(false);
          }
        }}
        disableCloseOnSelect
        slots={{
          paper(props) {
            return (
              <SearchBarFacets
                props={props}
                searchTerm={searchTerm}
                facets={facets}
                handleOpen={handleOpen}
              />
            );
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            hiddenLabel
            placeholder="Type to search..."
            InputProps={{
              ...params.InputProps,
              type: "search",
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    className="material-symbols-outlined"
                    aria-label="search"
                    onClick={() => {
                      handleSearch();
                    }}
                    sx={searchButtonStyles}
                  >
                    search
                  </IconButton>
                </InputAdornment>
              ),
              sx: textFieldStyles,
            }}
            inputProps={{
              ...params.inputProps,
              sx: {
                ml: 1,
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default BaseSearchBar;
